import { TShareWithManagerButtonState } from './types';

/**
 * CSS class definitions for the ShareWithManagerButton component based on its state.
 *
 * The styles are organized into arrays for each state, which are then combined
 * with the base styles. This approach allows for clear separation of styles based
 * on the button's state while maintaining a consistent base style across all states.
 *
 * The getButtonStyles function takes the current state of the button and
 * returns the appropriate combination of styles.
 */

/**
 * Base styles applied to all states for consistent layout and typography.
 */
const baseStyles = [
  'inline-flex',
  'items-center',
  'justify-center',
  'gap-2',
  'px-2',
  'py-0',
  'rounded-full',
  'font-medium',
  'text-sm',
  'transition-all',
  'duration-200',
  'ease-in-out',
  'border',
  'enabled:focus-visible:outline-2',
  'enabled:focus-visible:outline-offset-2',
  'enabled:focus-visible:outline-brand-primary',
  'typography-button-small',
  'whitespace-nowrap',
  '!font-normal',
];

/**
 * Styles for the 'sharing' state, indicating a successful share action with a
 * green background and white text.
 */
const sharingStyles = [
  ...baseStyles,
  'bg-accent-green-300',
  'border-accent-green-300',
  'text-neutral-white',
  'cursor-default',
];

/**
 * Styles for the 'error' state, indicating an error occurred with a red background
 * and red text.
 */
const errorStyles = [
  ...baseStyles,
  'bg-accent-surface-red',
  'border-accent-red-500',
  'text-accent-red-500',
  'cursor-default',
  'gap-1',
];

/**
 * Styles for both 'initial' and 'shared' states, which include interactive styles for
 * hover and focus effects. The 'shared' state will have the same styles but different content.
 */
const initialAndSharedStyles = [
  ...baseStyles,
  'active:border-neutral-gray-700',
  'border-transparent',
  'disabled:pointer-events-none',
  'disabled:no-underline',
  'disabled:text-neutral-gray-700',
  'hover:text-neutral-black',
  'text-neutral-gray-800',
  'underline-offset-4',
  'underline',
  'enabled:focus-visible:outline-brand-primary',
  'enabled:focus-visible:text-brand-primary',
  'enabled:hover:text-brand-primary',
  'rounded-sm',
  'enabled:focus-visible:outline-1',
  'enabled:focus-visible:outline-dashed',
  'enabled:focus-visible:outline-offset-2',
];

/**
 * Returns the appropriate CSS classes for the button based on its current state
 *
 * - The base styles are applied to all states for consistent layout and typography
 * - In the 'sharing' state, it applies styles to indicate success
 * - In the 'initial' and 'shared' states, it applies styles for hover and focus effects
 * - The 'shared' state matches 'initial' but with different content to indicate the shared status
 * - The 'error' state applies styles that indicate an error occurred
 */
export const getButtonStyles = (shareState: TShareWithManagerButtonState) => {
  switch (shareState) {
    case 'sharing':
      return sharingStyles;
    case 'error':
      return errorStyles;
    case 'initial':
    case 'shared':
    default:
      return initialAndSharedStyles;
  }
};
